import { Injectable } from '@angular/core';
import { IFilterListService } from './ifilter-list.service';
import { map } from 'rxjs';
import { SharedQueries } from 'src/app/shared/types/get-query';
import { Color } from 'src/app/components/company/models/new/style';
import { ColorMapper } from 'src/app/components/company/services/color-mapper';
import { Selectable } from 'src/app/shared/types/selectable.type';

@Injectable({
	providedIn: 'root'
})
export class ColorFilterService extends IFilterListService<Color> {
	private readonly PAGE_SIZE = 50;

	private currentSearch = '';
	private currentPage = 0;

	private hasNextPage = true;

	private currentVariables: any = {};
	private internalDatasource: Selectable<Color>[] = [];

	constructor(private colorMapper: ColorMapper) {
		super();
	}

	private toSelectable(node: any): Selectable<Color> {
		return {
			...this.colorMapper.toColor(node),
			displayId: node.id,
			displayName: `${node.code} - ${node.finalColorName}`,
			selected: false,
			disabled: false
		};
	}

	pageCursorCalculator(pageIndex: number, pageSize: number): string {
		return btoa((pageSize * pageIndex - 1).toString());
	}

	reset(): void {
		this.currentPage = 0;
		this.hasNextPage = true;
		this.internalDatasource = [];
		this.datasource.next([]);
	}

	search(searchText: string, variables?: any, append: boolean = false): void {
		if (this.loading) return;
		if (append && !this.hasNextPage) return;

		if (!append) {
			this.reset();
			this.currentSearch = searchText;

			// NON perdere mai le variables
			if (variables) {
				this.currentVariables = variables;
			}
		}

		const after = this.currentPage === 0 ? null : this.pageCursorCalculator(this.currentPage, this.PAGE_SIZE);

		const modelId = this.currentVariables?.modelId;

		const where: any = {
			and: [
				{
					finalColorName: {
						contains: this.currentSearch
					}
				}
			]
		};

		// aggiungi filtro SOLO se esiste davvero
		if (modelId != null) {
			where.and.push({
				schemaTeamColors: {
					some: {
						schema: {
							modelId: {
								eq: modelId
							}
						}
					}
				}
			});
		}

		this.loading = true;
		this.graphQlService
			.getData<any>(SharedQueries.GET_COLORS, {
				where,
				first: this.PAGE_SIZE,
				after
			})
			.pipe(
				map((res: any) => res.data.colors.nodes.map((node: any) => this.toSelectable(node)))
			)
			.subscribe({
				next: (items: Selectable<Color>[]) => {
				this.loading = false;
				if (!items.length) {
						this.hasNextPage = false;
						return;
					}

					const unique = items.filter((item) => !this.internalDatasource.some((el) => el.displayId === item.displayId));

					this.internalDatasource = [...this.internalDatasource, ...unique];

					this.datasource.next(this.internalDatasource);

					this.currentPage++;

					if (items.length < this.PAGE_SIZE) {
						this.hasNextPage = false;
					}

				},
				error: () => {
					this.loading = false;
				}
			});
	}

	loadNextPage(): void {
		this.search(this.currentSearch, this.currentVariables, true);
	}

	preloadUntilSelected(selected: Array<Selectable<Color>>, searchText: string = '', variables?: any): void {
		if (!selected?.length) {
			this.search(searchText, variables);
			return;
		}

		this.reset();

		this.currentVariables = variables || {};

		const targetIds = selected.map((el) => el.displayId);

		const recursiveLoad = () => {
			if (this.loading || !this.hasNextPage) return;

			const after = this.currentPage === 0 ? null : this.pageCursorCalculator(this.currentPage, this.PAGE_SIZE);

			this.loading = true;

			const modelId = this.currentVariables?.modelId;

			const where: any = {
				and: [
					{
						finalColorName: {
							contains: searchText
						}
					}
				]
			};

			if (modelId != null) {
				where.and.push({
					schemaTeamColors: {
						some: {
							schema: {
								modelId: {
									eq: modelId
								}
							}
						}
					}
				});
			}

			this.graphQlService
				.getData<any>(SharedQueries.GET_COLORS, {
					where,
					first: this.PAGE_SIZE,
					after
				})
				.pipe(
					map((res: any) => res.data.colors.nodes.map((node: any) => this.toSelectable(node)))
				)
				.subscribe({
					next: (items: Selectable<Color>[]) => {
						if (!items.length) {
							this.hasNextPage = false;
							this.loading = false;
							return;
						}

						const unique = items.filter((item) => !this.internalDatasource.some((el) => el.displayId === item.displayId));

						this.internalDatasource = [...this.internalDatasource, ...unique];

						this.datasource.next(this.internalDatasource);

						this.currentPage++;

						const foundAll = targetIds.every((id) => this.internalDatasource.some((el) => el.displayId === id));

						if (foundAll) {
							this.loading = false;
							return;
						}

						if (items.length < this.PAGE_SIZE) {
							this.hasNextPage = false;
							this.loading = false;
							return;
						}

						this.loading = false;
						recursiveLoad();
					},
					error: () => {
						this.loading = false;
					}
				});
		};

		recursiveLoad();
	}

	searchById(_id: string[] | string) {
		const ids = Array.isArray(_id) ? _id.map((id) => Number(id)) : [Number(_id)];

		return this.graphQlService
			.getData<any>(SharedQueries.GET_COLORS, {
				where: {
					id: {
						in: ids
					}
				},
				first: ids.length
			})
			.pipe(map((res) => res.data.colors.nodes.map((el: any) => this.colorMapper.toColor(el))));
	}
}